import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RecordingService } from 'src/app/services/recording.service';
import { DisplayMediaSourceOption } from 'src/app/services/platform.service';

export type RecordingSetupMode = 'audio' | 'screen';

export interface RecordingSetupDialogData {
  is_electron: boolean;
}

export interface RecordingSetupDialogResult {
  mode: RecordingSetupMode;
  include_microphone: boolean;
  microphone_device_id: string | null;
  electron_screen_source_id: string | null;
  electron_include_system_audio: boolean;
}

@Component({
  selector: 'app-recording-setup-dialog',
  templateUrl: './recording-setup-dialog.component.html',
  styleUrls: ['./recording-setup-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatRadioModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    FormsModule,
  ],
})
export class RecordingSetupDialogComponent implements OnInit {
  private dialog_ref = inject(MatDialogRef<RecordingSetupDialogComponent>);
  private recording_service = inject(RecordingService);
  private snack_bar = inject(MatSnackBar);
  private change_detector_ref = inject(ChangeDetectorRef);
  protected data = inject<RecordingSetupDialogData>(MAT_DIALOG_DATA);

  protected recording_mode: RecordingSetupMode = 'audio';
  protected include_microphone_with_screen = true;
  protected microphone_device_id: string | null = null;
  protected microphone_device_options: Array<{ device_id: string; label: string }> = [];
  protected is_loading_microphones = true;
  protected display_sources: DisplayMediaSourceOption[] = [];
  protected selected_display_source_id = '';
  protected include_system_audio = false;
  protected supports_system_audio = false;
  protected is_loading_display_sources = false;
  protected microphone_permission_message: string | null = null;
  protected screen_permission_message: string | null = null;

  ngOnInit(): void {
    void this.initializeDialog();
  }

  protected can_confirm(): boolean {
    if (this.recording_mode === 'audio') {
      return this.microphone_device_id !== null;
    }

    if (this.data.is_electron) {
      const has_valid_screen = this.selected_display_source_id !== '';

      if (!has_valid_screen) {
        return false;
      }
    }

    if (this.include_microphone_with_screen) {
      return this.microphone_device_id !== null;
    }

    return true;
  }

  confirm(): void {
    if (!this.can_confirm()) {
      return;
    }

    const is_audio_only = this.recording_mode === 'audio';

    this.dialog_ref.close({
      mode: this.recording_mode,
      include_microphone: is_audio_only || this.include_microphone_with_screen,
      microphone_device_id: is_audio_only || this.include_microphone_with_screen ? this.microphone_device_id : null,
      electron_screen_source_id: !is_audio_only && this.data.is_electron ? this.selected_display_source_id : null,
      electron_include_system_audio: !is_audio_only && this.data.is_electron && this.supports_system_audio && this.include_system_audio,
    } satisfies RecordingSetupDialogResult);
  }

  cancel(): void {
    this.dialog_ref.close();
  }

  private async initializeDialog(): Promise<void> {
    await this.loadMicrophoneDevices();

    if (this.data.is_electron) {
      await this.loadDisplaySources();
    }

    this.change_detector_ref.markForCheck();
  }

  private async loadMicrophoneDevices(): Promise<void> {
    this.is_loading_microphones = true;

    const microphone_result = await this.recording_service.loadMicrophoneDevices();
    this.microphone_device_options = microphone_result.devices;
    this.microphone_device_id = this.microphone_device_options[0]?.device_id ?? null;
    this.is_loading_microphones = false;
    this.microphone_permission_message = microphone_result.error_message;

    if (microphone_result.error_message !== null) {
      this.snack_bar.open(microphone_result.error_message, undefined, { duration: 7000 });
    }

    if (this.microphone_device_options.length === 0) {
      this.microphone_device_options = [];
      this.microphone_device_id = null;
    }
  }

  private async loadDisplaySources(): Promise<void> {
    this.is_loading_display_sources = true;
    const display_result = await this.recording_service.loadDisplaySourcesForRecordingSetup();
    this.display_sources = display_result.sources;
    this.supports_system_audio = display_result.supports_system_audio;
    this.selected_display_source_id = this.display_sources[0]?.id ?? '';
    this.include_system_audio = this.supports_system_audio;
    this.is_loading_display_sources = false;
    this.screen_permission_message = display_result.error_message;
  }
}
